import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import {
  fetchMedicareCertPrefill,
  type MedicareCertPrefillData,
  type MedicareCertPrefillError,
} from "@services/facilityManagement";
import { getApiOrigin } from "@utils/apiOrigin";
import { isGlobalAdministrator } from "@utils/userUtils";
import { CmsLookupAdminDebugPanel, type CmsAdminDebugSnapshot } from "./CmsLookupAdminDebugPanel";
import { CmsLookupOutcomeFailure, CmsLookupOutcomeSuccess } from "./cmsLookupOutcomeIcons";

export interface MedicareCertLookupButtonProps {
  facilityId: number;
  disabled?: boolean;
  onPrefill: (data: MedicareCertPrefillData) => void;
  /**
   * Optional caption under the Medicare fields (e.g. auto-prefill on load).
   * Cleared when starting a manual refresh.
   */
  onManualRefreshNote?: (note: string | null) => void;
}

function medicareCertPrefillRequestUrl(facilityId: number, forceRefresh: boolean): string {
  const base = getApiOrigin().replace(/\/+$/, "");
  const qs = forceRefresh ? "?force_refresh=true" : "";
  return `${base}/api/v1/facility-management/facility/${facilityId}/medicare-cert-prefill${qs}`;
}

/**
 * Loads Medicare certification / revalidation dates from CMS when the server runs a lookup.
 * Outcome is a compact check (success) or red X (failure) beside the button.
 * Global administrators also see request URL and response or error JSON below.
 */
const MedicareCertLookupButton: React.FC<MedicareCertLookupButtonProps> = ({
  facilityId,
  disabled = false,
  onPrefill,
  onManualRefreshNote,
}) => {
  const isAdmin = isGlobalAdministrator();
  const [loading, setLoading] = useState(false);
  const [httpError, setHttpError] = useState<MedicareCertPrefillError | null>(null);
  /** Inline outcome next to the button (persists until the next lookup). */
  const [outcomeBadge, setOutcomeBadge] = useState<"success" | "error" | null>(null);
  const [adminDebug, setAdminDebug] = useState<CmsAdminDebugSnapshot | null>(null);

  const runLookup = async () => {
    setLoading(true);
    setHttpError(null);
    setOutcomeBadge(null);
    setAdminDebug(null);
    onManualRefreshNote?.(null);
    const requestUrl = medicareCertPrefillRequestUrl(facilityId, true);
    try {
      const result = await fetchMedicareCertPrefill(facilityId, { forceRefresh: true });
      if (result.ok) {
        onPrefill(result.data);
        setOutcomeBadge("success");
        if (isAdmin) {
          setAdminDebug({
            outcome: "success",
            requestUrl,
            responseBody: result.data,
          });
        }
      } else {
        setOutcomeBadge("error");
        setHttpError(result);
        if (isAdmin) {
          setAdminDebug({
            outcome: "error",
            requestUrl: result.url || requestUrl,
            status: result.status,
            message: result.message,
            error_code: result.error_code,
            detail: result.detail,
          });
        }
      }
    } catch (e) {
      setOutcomeBadge("error");
      const err: MedicareCertPrefillError = {
        ok: false,
        status: 0,
        url: requestUrl,
        message: e instanceof Error ? e.message : "Lookup failed",
      };
      setHttpError(err);
      if (isAdmin) {
        setAdminDebug({
          outcome: "error",
          requestUrl,
          status: 0,
          message: err.message,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const userFacingHttpMessage = (err: MedicareCertPrefillError): string => {
    if (err.status === 401 || err.status === 403) {
      return "You are not allowed to refresh Medicare data for this facility, or your session expired. Sign in again or ask an administrator.";
    }
    if (err.status === 404) {
      return "The Medicare refresh service was not found. Your app may be pointed at the wrong server version.";
    }
    if (err.status >= 500) {
      return "The server had a problem completing the Medicare refresh. Try again in a few minutes.";
    }
    if (!err.status) {
      return (
        err.message ||
        "The Medicare refresh could not be completed. Check your connection and try again."
      );
    }
    return err.message || `The Medicare refresh failed (HTTP ${err.status}).`;
  };

  const errorTooltipTitle =
    httpError != null ? userFacingHttpMessage(httpError) : "CMS lookup failed";

  return (
    <Box sx={{ mt: 1, width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Button
          type="button"
          variant="outlined"
          size="small"
          disabled={disabled || loading}
          onClick={runLookup}
          sx={{
            textTransform: "none",
            fontFamily: "Rethink Sans, sans-serif",
            borderColor: "#0e7490",
            color: "#0e7490",
          }}
        >
          {loading ? "Calling CMS…" : "Get Medicare dates from CMS"}
        </Button>
        {outcomeBadge === "success" && !loading ? (
          <CmsLookupOutcomeSuccess
            title="Medicare dates were updated from CMS"
            ariaLabel="Medicare CMS lookup succeeded"
          />
        ) : null}
        {outcomeBadge === "error" && !loading && httpError ? (
          <CmsLookupOutcomeFailure title={errorTooltipTitle} ariaLabel="Medicare CMS lookup failed" />
        ) : null}
      </Box>
      {isAdmin ? (
        <CmsLookupAdminDebugPanel
          title="Medicare cert prefill — debug (global administrator)"
          snapshot={adminDebug}
        />
      ) : null}
    </Box>
  );
};

export default MedicareCertLookupButton;
